import { useInfiniteQuery, useQuery } from "@tanstack/react-query";

import { apiGetV2 } from "~/shared/api/client";

import { userKeys } from "./profile-keys";

export interface AvatarCrop {
    unit: "%";
    x: number;
    y: number;
    width: number;
    height: number;
}

export interface CurrentUser {
    id: string;
    firstName: string | null;
    surname: string | null;
    name: string | null;
    email: string;
    image: string | null;
    imageOriginal: string | null;
    imageCrop: AvatarCrop | null;
    isSuperAdmin: boolean;
    status: string;
    lastLoginAt: string | null;
    lastActionAt: string | null;
    createdAt: string | null;
}

export function fetchCurrentUser(): Promise<CurrentUser> {
    return apiGetV2<CurrentUser>("/users/me");
}

export function useCurrentUser(options?: { enabled?: boolean }) {
    return useQuery({
        queryKey: userKeys.me,
        queryFn: fetchCurrentUser,
        enabled: options?.enabled ?? true,
    });
}

// ---------------------------------------------------------------------------
// Поиск пользователей (доступен только суперадмину — бэкенд отвечает 403 остальным)
// ---------------------------------------------------------------------------

export interface UserSearchItem {
    id: string;
    email: string;
    name: string | null;
    firstName: string | null;
    surname: string | null;
    image: string | null;
}

export interface UserSearchPage {
    items: UserSearchItem[];
    total: number;
}

/** Размер страницы: столько же строк догружается при прокрутке списка. */
export const USER_SEARCH_PAGE_SIZE = 20;

export function fetchUserSearch(search: string, offset = 0): Promise<UserSearchPage> {
    const trimmed = search.trim();
    return apiGetV2<UserSearchPage>("/users/search", {
        // Бэкенд требует непустую подстроку: пустая строка — это «все пользователи»,
        // параметр не передаётся вовсе.
        ...(trimmed ? { search: trimmed } : {}),
        limit: USER_SEARCH_PAGE_SIZE,
        offset,
    });
}

/**
 * Серверный поиск пользователей по подстроке email/имени или точному UUID.
 *
 * Постранично: следующая страница берётся по числу уже загруженных строк, пока
 * их меньше, чем `total`. Пустой поиск — первая страница всех пользователей.
 */
export function useUserSearch(search: string, options?: { enabled?: boolean }) {
    return useInfiniteQuery({
        queryKey: userKeys.search(search.trim()),
        queryFn: ({ pageParam }) => fetchUserSearch(search, pageParam),
        initialPageParam: 0,
        getNextPageParam: (lastPage, allPages) => {
            const loaded = allPages.reduce((count, page) => count + page.items.length, 0);
            return loaded < lastPage.total && lastPage.items.length > 0 ? loaded : undefined;
        },
        enabled: options?.enabled ?? true,
    });
}
